import pluralize from 'pluralize';

import { PropertyStatus } from '@tallo/property';

import { PropertyListItemDto } from '../dtos/property-list-item.dto.interface';

export const allPortfoliosFilter = 'all';
export const unassignedPortfolioFilter = 'unassigned';

/** `all`, `unassigned`, or a portfolio id */
export type PortfolioFilter = string;

/** Anything filed into a portfolio, or into none */
type Filed = { portfolio: { id: string } | null };

/** Units and buildings alike */
export function filterByPortfolio<T extends { portfolio: { id: string } | null }>(
  items: T[],
  filter: PortfolioFilter,
): T[] {
  switch (filter) {
    case allPortfoliosFilter:
      return items;

    case unassignedPortfolioFilter:
      return items.filter((item) => !item.portfolio);

    default:
      return items.filter((item) => item.portfolio?.id === filter);
  }
}

/** How many units each portfolio holds on the page, plus the ones in none under `unassigned` */
export function countByPortfolio(properties: PropertyListItemDto[]): Record<string, number> {
  const counts: Record<string, number> = { [unassignedPortfolioFilter]: 0 };

  for (const property of properties) {
    const key = property.portfolio?.id ?? unassignedPortfolioFilter;

    counts[key] = (counts[key] ?? 0) + 1;
  }

  return counts;
}

/** What a scope holds, the two kinds apart: units live under their building and are not counted here */
export interface Holdings {
  homes: number;
  buildings: number;
}

export const noHoldings: Holdings = { homes: 0, buildings: 0 };

/** Each portfolio's holdings, plus the loose ones under `unassigned` */
export function countHoldingsByPortfolio(homes: Filed[], buildings: Filed[]): Record<string, Holdings> {
  const counts: Record<string, Holdings> = { [unassignedPortfolioFilter]: { ...noHoldings } };
  const add = (item: Filed, kind: keyof Holdings) => {
    const key = item.portfolio?.id ?? unassignedPortfolioFilter;

    counts[key] = { ...(counts[key] ?? noHoldings), [kind]: (counts[key]?.[kind] ?? 0) + 1 };
  };

  homes.forEach((home) => add(home, 'homes'));
  buildings.forEach((building) => add(building, 'buildings'));

  return counts;
}

export function holdingsTotal({ homes, buildings }: Holdings): number {
  return homes + buildings;
}

/** "4 homes, 3 buildings"; "4 properties" while there is no building to tell apart from; "No properties yet" */
export function holdingsLabel({ homes, buildings }: Holdings): string {
  if (!homes && !buildings) {
    return 'No properties yet';
  }

  if (!buildings) {
    return pluralize('property', homes, true);
  }

  return [homes ? pluralize('home', homes, true) : '', pluralize('building', buildings, true)]
    .filter(Boolean)
    .join(', ');
}

/** The status chips' counts, over whatever the page is showing */
export function countByStatus(properties: PropertyListItemDto[]): Partial<Record<PropertyStatus, number>> {
  const counts: Partial<Record<PropertyStatus, number>> = {};

  for (const property of properties) {
    counts[property.status] = (counts[property.status] ?? 0) + 1;
  }

  return counts;
}
